import * as React from 'react';
import {
  Paper,
  Button,
  Box,
  Typography,
  Dialog,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import Video from '../components/Video';
import Segments, { SignedUrl } from '../components/Segments';
import { ImageMagnifier } from '../components/Dashboard/Image';
import backendApi from '../services/axios.auth.wrapper';
import { useQuery } from '@tanstack/react-query';

import { useRecoilState } from 'recoil';
import {
  dateRangeState,
  dateValueState,
  siteState,
  cameraState,
  selectedOrdersState,
  selectedSpeciesState,
  dateRangeAdjustedState,
  speciesListState,
  speciesFilterModeState,
} from '../state/statistics.state';
import { ReportNotABirdSuccessDialog } from '../components/Dashboard/ReportNotABirdSuccessDialog';
import { ReportCollisionSuccessDialog } from '../components/Dashboard/ReportCollisionSuccessDialog';
import { GetCameraData, GetSiteData, GetSpeciesList } from '../hooks/statistics.hooks';
import { GetLastProcessedDetection } from '../hooks/dashboard.hooks';
import { getCurrentUserWithIdentification } from '../services/auth.service';
import User from '../types/user.type';
import { useInitializeSiteAndCamera } from '../hooks/useInitializeSiteAndCamera';
import { useOptimalDateRange } from '../hooks/useOptimalDateRange';
import { StaticticsStickyNavbar } from '../components/statistics/StatisticsStickyNavbar';

const baseURL = process.env.REACT_APP_BACKEND_URL;

// Styled components for consistent button styling
const StyledButtonContainer = styled('div')(({ theme }) => ({
  marginTop: 24,
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  gap: '16px',
}));

const StyledPrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: '10px 20px',
  fontWeight: '400',
  textTransform: 'none',
  fontSize: '13px',
  minWidth: '120px',
  border: '1px solid #45009C',
  color: '#45009C',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 2px rgba(69, 0, 156, 0.08)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    border: '1px solid #45009C',
    backgroundColor: 'rgba(69, 0, 156, 0.04)',
    boxShadow: '0 2px 8px rgba(69, 0, 156, 0.15)',
    transform: 'translateY(-0.5px)',
  },
  '&:active': {
    transform: 'translateY(0px)',
    boxShadow: '0 1px 3px rgba(69, 0, 156, 0.1)',
  }
}));

function DashboardContent() {
  const [site, setSite] = useRecoilState(siteState);
  const [camera, setCamera] = useRecoilState(cameraState);
  const [dateRange, setDateRange] = useRecoilState(dateRangeState);
  const [dateValue, setDateValue] = useRecoilState(dateValueState);
  const [selectedSpecies, setSelectedSpecies] = useRecoilState(selectedSpeciesState);
  const [selectedOrders, setSelectedOrders] = useRecoilState(selectedOrdersState);
  const [species, setSpecies] = useRecoilState(speciesListState);
  const [speciesFilterMode, setSpeciesFilterMode] = useRecoilState(speciesFilterModeState);
  const [segmentMp4UrlDetails, setSegmentMp4UrlDetails] =
    React.useState<SignedUrl | null>(null);
  const [segmentDetails, setSegmentDetails] = React.useState({ url: '', camera_id: null });
  const [trackDetails, setTrackDetails] = React.useState({ url: '', camera_id: null, segment_name: '' });
  const [hasAdjustedDateRange, setHasAdjustedDateRange] = useRecoilState(dateRangeAdjustedState);
  const { data: siteData } = GetSiteData();
  const { data: cameraData } = GetCameraData(site);

  const shouldFilterSpecies =
    speciesFilterMode === 'custom' && (selectedOrders.length > 0 || selectedSpecies.length > 0);
  const filteredOrders = shouldFilterSpecies ? selectedOrders : [];
  const filteredSpecies = shouldFilterSpecies ? selectedSpecies : [];

  const { data: lastProcessedData } = GetLastProcessedDetection(site);
  const lastProcessedTimestamp = lastProcessedData?.data?.segment_timestamp ?? null;

  useOptimalDateRange({
    lastProcessedTimestamp,
    hasAdjustedDateRange,
    setHasAdjustedDateRange,
  });
  
  GetSpeciesList(setSpecies, dateRange, site, camera);
  const [showPossibleCollisionSuccessDialog, setShowPossibleCollisionSuccessDialog] = React.useState(false);
  const [showNotABirdSuccessDialog, setShowNotABirdSuccessDialog] = React.useState(false);
  const [notABirdSuccessMessage, setNotABirdSuccessMessage] = React.useState('');
  const [collisionSuccessMessage, setCollisionSuccessMessage] = React.useState('');

  const segmentIsThermal = React.useMemo(() => {
    if (!segmentDetails.camera_id || !cameraData?.data || !Array.isArray(cameraData.data)) return false;
    const cam = cameraData.data.find((c: any) => c.id === segmentDetails.camera_id);
    return cam?.features?.includes("thermal");
  }, [segmentDetails.camera_id, cameraData]);

  const trackIsThermal = React.useMemo(() => {
    if (!trackDetails.camera_id || !cameraData?.data || !Array.isArray(cameraData.data)) return false;
    const cam = cameraData.data.find((c: any) => c.id === trackDetails.camera_id);
    return cam?.features?.includes("thermal");
  }, [trackDetails.camera_id, cameraData]);

  const [showNotABirdErrorDialog, setShowNotABirdErrorDialog] = React.useState(false);
  const [notABirdErrorMessage, setNotABirdErrorMessage] = React.useState('');
  const [showCollisionErrorDialog, setShowCollisionErrorDialog] = React.useState(false);
  const [collisionErrorMessage, setCollisionErrorMessage] = React.useState('');
  const [isNotABirdLoading, setIsNotABirdLoading] = React.useState(false);
  const [isCollisionLoading, setIsCollisionLoading] = React.useState(false);

  const handleNotABirdFeedback = async () => {
    setIsNotABirdLoading(true);
    try {
      const userdetails = await getCurrentUserWithIdentification() as User;
      const userEmail = localStorage.getItem('userEmail') || userdetails.name;
      
      const response = await backendApi({
        method: 'post',
        url: `${baseURL}/api/mislabelled_segment`,
        data: {
          segment_name: trackDetails.segment_name,
          site_id: Number(site),
          username: userEmail,
        },
      });
      
      setNotABirdSuccessMessage(response.data.message || 'Report submitted successfully!');
      setShowNotABirdSuccessDialog(true);
    } catch (error: any) {
      if (error.response?.status === 503) {
        setNotABirdErrorMessage('Database table not available. Please contact support.');
      } else {
        setNotABirdErrorMessage('Failed to submit report. Please try again.');
      }
      setShowNotABirdErrorDialog(true);
    } finally {
      setIsNotABirdLoading(false);
    }
  };

  const handleCollisionFeedback = async () => {
    setIsCollisionLoading(true);
    try {
      const userdetails = await getCurrentUserWithIdentification() as User;
      const userEmail = localStorage.getItem('userEmail') || userdetails.name;
      const response = await backendApi({
        method: 'post',
        url: `${baseURL}/api/report_collision_to_slack`,
        data: {
          site_id: Number(site),
          username: userEmail,
          segment: [segmentMp4UrlDetails?.object_name ? segmentMp4UrlDetails.object_name.split('/')[1] : "Unknown"],
        },
      });
      setCollisionSuccessMessage(response.data.message || 'Collision report submitted successfully!');
      setShowPossibleCollisionSuccessDialog(true);
    } catch (error: any) {
      if (error.response?.status === 503) {
        setCollisionErrorMessage('Database table not available. Please contact support.');
      } else {
        setCollisionErrorMessage('Failed to submit collision report. Please try again.');
      }
      setShowCollisionErrorDialog(true);
    } finally {
      setIsCollisionLoading(false);
    }
  };

  const handleCollisionClose = () => {
    setShowPossibleCollisionSuccessDialog(false);
  };

  useQuery(
    ['latest-segment'],
    () => {
      return backendApi({
        method: 'get',
        url: `${baseURL}/api/segments/latest`,
      });
    },
    {
      onSuccess: (data) => {
        if (!dateRange.startDate) {
          if (data.data === '') {
            setDateRange({
              startDate: new Date(),
              endDate: new Date(),
            });
          } else {
            const oneMonthBack = new Date(data.data.segment_timestamp);
            oneMonthBack.setMonth(oneMonthBack.getMonth() - 1);
            setDateRange({
              startDate: oneMonthBack,
              endDate: new Date(data.data.segment_timestamp),
            });
          }
        }
      },
    },
  );

  useInitializeSiteAndCamera();

  const { data: segmentMp4Url } = useQuery(
    ['segment-mp4-url', segmentMp4UrlDetails],
    () => {
      if (segmentMp4UrlDetails && segmentMp4UrlDetails.bucket_name) {
        return backendApi({
          method: 'post',
          url: `${baseURL}/api/signedurl`,
          data: segmentMp4UrlDetails,
        });
      }
      return null;
    },
    {
      enabled: Boolean(segmentMp4UrlDetails?.bucket_name && segmentMp4UrlDetails?.object_name),
    }
  );

  const changeSegmentDetails = (url: any) => {
    if (url?.data) {
      setSegmentDetails({ url: url.data, camera_id: url.camera_id ?? null });
    } else {
      setSegmentDetails({ url: '', camera_id: null });
    }
  };

  const changeTrackDetails = (url: any) => {
    if (!url) {
      setTrackDetails({ url: '', camera_id: null, segment_name: '' });
      return;
    }
    
    setTrackDetails({
      url: url.data,
      camera_id: url.camera_id ?? null,
      segment_name: url.segment_name ?? ''
    });
  };

  const changeSegmentMp4Details = (signedUrl: SignedUrl) => {
    if (!signedUrl || !signedUrl.bucket_name) {
      setSegmentMp4UrlDetails(null);
      return;
    }
    setSegmentMp4UrlDetails(signedUrl);
  };

  const updateCameraDetails = (event: any, newValue: any) => {
    setCamera(newValue);
  };

  const downloadVideo = (url: string) => {
    if (!url) {
      console.warn('No URL provided for download');
      return;
    }
    const link = document.createElement('a');
    link.download = url;
    link.href = url;
    link.click();
  };

  return (
    <Box
      sx={{
        backgroundColor: '#F8F9FA',
        flexGrow: 1,
        paddingTop: '50px',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Box sx={{ width: '25%' }}></Box>
        <Box sx={{
          display: 'grid',
          flexDirection: 'column',
          rowGap: '8px',
          width: '1000px',
        }}>
          <h1 style={{
            fontSize: '32px',
            lineHeight: '40px',
            margin: 0,
            fontWeight: '600',
            color: '#17052D',
            letterSpacing: '-0.5px',
          }}>
            Recent Events
          </h1>
          <p style={{
            fontSize: '16px',
            lineHeight: '24px',
            color: '#6B7280',
            margin: '8px 0 0 0',
            fontWeight: '400',
          }}>
            Latest bird detection events and video segments
          </p>
        </Box>
      </Box>

      <StaticticsStickyNavbar
        dateRange={dateRange}
        setDateRange={setDateRange}
        site={site}
        setSite={setSite}
        listOfSites={siteData}
        updateCameraDetails={updateCameraDetails}
        cameraData={cameraData}
        camera={camera}
        speciesList={species}
        setSelectedOrders={setSelectedOrders}
        setSelectedSpecies={setSelectedSpecies}
        selectedOrders={selectedOrders}
        selectedSpecies={selectedSpecies}
        dateValue={dateValue}
        setDateValue={setDateValue}
        showDownloadButtons={false}
        setDateRangeAdjusted={setHasAdjustedDateRange}
        setSpeciesFilterMode={setSpeciesFilterMode}
      />

      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Box sx={{ width: '25%' }}></Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '1000px',
          paddingTop: '24px',
          paddingBottom: '48px',
          gap: '32px',
        }}>
          <ReportNotABirdSuccessDialog
            open={showNotABirdSuccessDialog}
            onClose={() => setShowNotABirdSuccessDialog(false)}
            segment_name={trackDetails.segment_name}
            message={notABirdSuccessMessage}
          />
          
          <Dialog
            onClose={() => setShowNotABirdErrorDialog(false)}
            open={showNotABirdErrorDialog}
            sx={{
              '& .MuiDialog-container': {
                '& .MuiPaper-root': {
                  width: '100%',
                  maxWidth: '480px',
                  borderRadius: '16px',
                  boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
                  border: '1px solid rgba(229, 231, 235, 0.8)',
                  background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
                  overflow: 'hidden',
                },
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                padding: '32px',
                textAlign: 'center',
              }}
            >
              <IconButton
                aria-label="close"
                onClick={() => setShowNotABirdErrorDialog(false)}
                sx={{
                  position: 'absolute',
                  right: '16px',
                  top: '16px',
                  color: '#8B8296',
                  '&:hover': {
                    backgroundColor: 'rgba(139, 130, 150, 0.08)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <Box
                  sx={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0px 4px 16px rgba(239, 68, 68, 0.3)',
                  }}
                >
                  <CloseIcon
                    sx={{
                      fontSize: '32px',
                      color: '#ffffff',
                    }}
                  />
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    color: '#17052D',
                    fontWeight: 600,
                    fontSize: '20px',
                    lineHeight: '28px',
                  }}
                >
                  Report Failed
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: '#6B7280',
                    fontSize: '14px',
                    lineHeight: '20px',
                    maxWidth: '320px',
                  }}
                >
                  {notABirdErrorMessage}
                </Typography>

                <Button
                  variant="contained"
                  onClick={() => setShowNotABirdErrorDialog(false)}
                  sx={{
                    marginTop: '8px',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    fontSize: '14px',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #45009C 0%, #7C3AED 100%)',
                    boxShadow: '0px 2px 8px rgba(69, 0, 156, 0.25)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #7C3AED 0%, #45009C 100%)',
                      boxShadow: '0px 4px 12px rgba(69, 0, 156, 0.35)',
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                  }}
                >
                  Try Again
                </Button>
              </Box>
            </Box>
          </Dialog>
          
          <Dialog
            onClose={() => setShowCollisionErrorDialog(false)}
            open={showCollisionErrorDialog}
            sx={{
              '& .MuiDialog-container': {
                '& .MuiPaper-root': {
                  width: '100%',
                  maxWidth: '480px',
                  borderRadius: '16px',
                  boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
                  border: '1px solid rgba(229, 231, 235, 0.8)',
                  background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
                  overflow: 'hidden',
                },
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                padding: '32px',
                textAlign: 'center',
              }}
            >
              <IconButton
                aria-label="close"
                onClick={() => setShowCollisionErrorDialog(false)}
                sx={{
                  position: 'absolute',
                  right: '16px',
                  top: '16px',
                  color: '#8B8296',
                  '&:hover': {
                    backgroundColor: 'rgba(139, 130, 150, 0.08)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <Box
                  sx={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0px 4px 16px rgba(239, 68, 68, 0.3)',
                  }}
                >
                  <CloseIcon
                    sx={{
                      fontSize: '32px',
                      color: '#ffffff',
                    }}
                  />
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    color: '#17052D',
                    fontWeight: 600,
                    fontSize: '20px',
                    lineHeight: '28px',
                  }}
                >
                  Report Failed
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: '#6B7280',
                    fontSize: '14px',
                    lineHeight: '20px',
                    maxWidth: '320px',
                  }}
                >
                  {collisionErrorMessage}
                </Typography>

                <Button
                  variant="contained"
                  onClick={() => setShowCollisionErrorDialog(false)}
                  sx={{
                    marginTop: '8px',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    fontSize: '14px',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #45009C 0%, #7C3AED 100%)',
                    boxShadow: '0px 2px 8px rgba(69, 0, 156, 0.25)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #7C3AED 0%, #45009C 100%)',
                      boxShadow: '0px 4px 12px rgba(69, 0, 156, 0.35)',
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                  }}
                >
                  Try Again
                </Button>
              </Box>
            </Box>
          </Dialog>
          
          <ReportCollisionSuccessDialog
            open={showPossibleCollisionSuccessDialog}
            onClose={handleCollisionClose}
            segment_name={segmentMp4UrlDetails?.object_name && segmentMp4UrlDetails.object_name.includes('/') ? segmentMp4UrlDetails.object_name.split('/')[1] : "Unknown"}
            message={collisionSuccessMessage}
          />

          {segmentDetails.url ? (
            <Paper
              sx={{
                p: 4,
                borderRadius: '16px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(229, 231, 235, 0.8)',
                background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
              }}
            >
              <Box id="video-section">
                <Typography sx={{ color: '#17052D' }} variant="h6">
                  Video
                </Typography>
                <Typography
                  sx={{ color: '#8B8296', fontSize: '14px' }}
                  variant="body2"
                >
                  Video that captures the moment when a bird is detected by the system. 
                  It shows the bird's movement and behavior during the detection event.
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
                marginTop: '20px'
              }}>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  {segmentIsThermal && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        backgroundColor: 'rgba(255, 193, 7, 0.92)',
                        color: '#fff',
                        px: 2,
                        py: 0.5,
                        borderRadius: '16px',
                        fontWeight: 500,
                        fontSize: '13px',
                        zIndex: 10,
                        pointerEvents: 'none',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                        width: 'fit-content',
                        height: 'fit-content',
                        minWidth: 0,
                        minHeight: 0,
                        display: 'inline-block',
                        textAlign: 'center',
                      }}
                    >
                      Experimental feature
                    </Box>
                  )}
                  <Box>
                    <Video url={segmentDetails.url} />
                  </Box>
                  <StyledButtonContainer>
                    <StyledPrimaryButton
                      variant="outlined"
                      onClick={handleCollisionFeedback}
                      disabled={isCollisionLoading}
                    >
                      {isCollisionLoading ? 'Submitting...' : 'Collision?'}
                    </StyledPrimaryButton>
                    <StyledPrimaryButton
                      variant="outlined"
                      onClick={() => downloadVideo(segmentMp4Url?.data)}
                      disabled={!segmentMp4Url?.data}
                    >
                      Export
                    </StyledPrimaryButton>
                  </StyledButtonContainer>
                </Box>
              </Box>
            </Paper>
          ) : null}

          {trackDetails.url ? (
            <Paper
              sx={{
                p: 4,
                borderRadius: '16px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(229, 231, 235, 0.8)',
                background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
              }}
            >
              <Box id="image-section">
                <Typography sx={{ color: '#17052D' }} variant="h6">
                  Image
                </Typography>
                <Typography
                  sx={{ color: '#8B8296', fontSize: '14px' }}
                  variant="body2"
                >
                  Composite image that shows the complete flight path of a detected bird and cropped images of the bird.
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
                marginTop: '20px'
              }}>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  {trackIsThermal && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        backgroundColor: 'rgba(255, 193, 7, 0.92)',
                        color: '#fff',
                        px: 2,
                        py: 0.5,
                        borderRadius: '16px',
                        fontWeight: 500,
                        fontSize: '13px',
                        zIndex: 10,
                        pointerEvents: 'none',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                        width: 'fit-content',
                        height: 'fit-content',
                        minWidth: 0,
                        minHeight: 0,
                        display: 'inline-block',
                        textAlign: 'center',
                      }}
                    >
                      Experimental feature
                    </Box>
                  )}
                  <ImageMagnifier src={trackDetails.url} />
                  <StyledButtonContainer>
                    <StyledPrimaryButton
                      variant="outlined"
                      onClick={handleNotABirdFeedback}
                      disabled={isNotABirdLoading}
                    >
                      {isNotABirdLoading ? 'Submitting...' : 'Not a Bird?'}
                    </StyledPrimaryButton>
                    <StyledPrimaryButton
                      variant="outlined"
                      onClick={() => downloadVideo(trackDetails.url)}
                      disabled={!trackDetails.url}
                    >
                      Export
                    </StyledPrimaryButton>
                  </StyledButtonContainer>
                </Box>
              </Box>
            </Paper>
          ) : null}

          <Paper
            sx={{
              p: 4,
              borderRadius: '16px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(229, 231, 235, 0.8)',
              background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
            }}
          >
            <Box id="detections-section">
              <Typography sx={{ color: '#17052D' }} variant="h6">
                Detections
              </Typography>
              <Typography
                sx={{ color: '#8B8296', fontSize: '14px' }}
                variant="body2"
              >
                List of recent bird detections with detailed information about each event.
              </Typography>
            </Box>
            <Box sx={{ marginTop: '20px' }}>
              <Segments
                site={site}
                camera={camera}
                dateRange={dateRange}
                onSegmentChange={changeSegmentDetails}
                onTrackChange={changeTrackDetails}
                onSegmentMp4Change={changeSegmentMp4Details}
                selectedOrders={filteredOrders}
                selectedSpecies={filteredSpecies}
              />
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

export default function RecentEvents() {
  return <DashboardContent />;
}
